import { bubbleBox, BUBBLE_LH } from "./bubbleText";
import { col, toPath, drawTextT, bboxOfPts } from "./pdfCore";
import { bubblePaint, bubbleFont } from "./bubbleStyle";

function rrect(x, y, w, h, rx, ry) {
    const pts = [];
    const corner = (cx, cy, a0) => { for (let i = 0; i <= 8; i++) { const a = a0 + (i / 8) * (Math.PI / 2); pts.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)]); } };
    corner(x + w - rx, y + ry, -Math.PI / 2);
    corner(x + w - rx, y + h - ry, 0);
    corner(x + rx, y + h - ry, Math.PI / 2);
    corner(x + rx, y + ry, Math.PI);
    return pts;
}

// Same geometry as the on-screen bubble (PdfViewer.renderBubble). u.base = page width * 0.03. Returns PDF rect.
export async function drawBubble(target, u, st, map, fonts) {
    const px = u.base * st.scale;
    const box = bubbleBox(u.label, px, st);
    const { w, h } = box;
    const ccx = u.cx, ccy = u.cy - px / 2 + h / 2;
    const rot = (((st.rotation || 0) + (st.shape === "diamond" ? 45 : 0)) * Math.PI) / 180;
    const T = ([lx, ly]) => { const x = lx - w / 2, y = ly - h / 2; return [ccx + x * Math.cos(rot) - y * Math.sin(rot), ccy + x * Math.sin(rot) + y * Math.cos(rot)]; };
    const paint = bubblePaint(st, u.color, px);
    const bw = paint.borderWidth;
    const m = Math.min(w, h);
    const [rx, ry] = st.shape === "circle" ? [m / 2, m / 2] : st.shape === "rounded" ? [w * 0.3, h * 0.3] : [m * 0.12, m * 0.12];
    const outer = rrect(0, 0, w, h, rx, ry).map(T);
    const inner = rrect(bw / 2, bw / 2, w - bw, h - bw, Math.max(0.01, rx - bw / 2), Math.max(0.01, ry - bw / 2)).map(T);
    if (paint.fill && paint.fillOpacity > 0) target.drawSvgPath(toPath(outer, map, true), { x: 0, y: 0, color: col(paint.fill), opacity: paint.fillOpacity });
    if (bw > 0 && paint.borderOpacity > 0) target.drawSvgPath(toPath(inner, map, true), { x: 0, y: 0, borderColor: col(paint.border), borderOpacity: paint.borderOpacity, borderWidth: bw });

    const f = await fonts.get(bubbleFont(st.fontFamily).pdf, st.bold, st.italic);
    const fs = box.fs, lh = fs * BUBBLE_LH;
    const left = bw + box.padX, right = w - bw - box.padX;
    const innerH = h - 2 * (bw + box.padY), textH = box.lines.length * lh;
    const slack = innerH - textH;
    const top0 = bw + box.padY + (slack < 0 || st.alignV === "top" ? 0 : st.alignV === "bottom" ? slack : slack / 2);
    const color = col(st.textColor);
    box.lines.forEach((ln, i) => {
        if (slack < 0 && top0 + (i + 1) * lh > h - bw + 0.5) return;
        const lw = f.widthOfTextAtSize(ln, fs);
        const lx = st.alignH === "left" ? left : st.alignH === "right" ? right - lw : (left + right - lw) / 2;
        const base = top0 + i * lh + (lh - fs) / 2 + 0.8 * fs;
        drawTextT(target, ln, T([lx, base]), T([lx + 1, base]), map, { size: fs, font: f, color });
        const deco = [st.underline && base + fs * 0.12, st.strike && base - fs * 0.28].filter((v) => v !== false && v !== undefined);
        deco.forEach((yy) => {
            const [a, b] = [map(T([lx, yy])), map(T([lx + lw, yy]))];
            target.drawLine({ start: { x: a[0], y: a[1] }, end: { x: b[0], y: b[1] }, thickness: Math.max(0.3, fs / 14), color });
        });
    });
    return bboxOfPts(outer.map(map), 0.5);
}

export function bubbleDetails(u, row, columns) {
    const lines = [u.label];
    (columns || []).forEach((c) => {
        if (c.type === "calc" || c.key === "blindPos" || c.hidden) return;
        const v = row?.[c.key];
        if (v !== undefined && v !== null && String(v).trim() !== "") lines.push(`${c.label}: ${v}`);
    });
    return lines.join("\n");
}
